<template>
	<div class="rounded bg-white p-3" style="border:1px solid #e2e8f0;">
		<div class="d-flex align-items-baseline justify-content-between mb-2">
			<h3 class="h6 fw-semibold mb-0" style="color:#0f172a;">Bulk upload — PDFs + JPEGs</h3>
			<span style="font-size:10px;color:#64748b;">Multi-page JPEGs are auto-grouped by filename.</span>
		</div>

		<!-- Drop zone -->
		<div
			@dragover.prevent="dragOver = true"
			@dragleave="dragOver = false"
			@drop.prevent="onDrop"
			class="rounded text-center"
			:style="{
				border: '2px dashed ' + (dragOver ? '#3b82f6' : '#cbd5e1'),
				background: dragOver ? '#eff6ff' : '#f8fafc',
				padding: '24px',
				transition: 'background 0.12s, border-color 0.12s',
			}"
		>
			<p class="mb-2" style="font-size:14px;color:#475569;">Drop PDFs and/or JPEGs here, or</p>
			<label class="btn btn-primary fw-medium mb-0" style="font-size:12px;padding:6px 12px;cursor:pointer;">
				Choose files
				<input type="file" multiple accept=".pdf,application/pdf,image/*" @change="onFilePick" class="d-none" />
			</label>
			<p class="mt-2 mb-0" style="font-size:10px;color:#64748b;">
				Grouping:
				<code style="background:white;padding:1px 4px;border-radius:3px;">Forestville-1.jpg</code>,
				<code style="background:white;padding:1px 4px;border-radius:3px;">Forestville-2.jpg</code>,
				<code style="background:white;padding:1px 4px;border-radius:3px;">Forestville-3.jpg</code>
				→ one proposal.
			</p>
		</div>

		<!-- Detected groups -->
		<div v-if="groups.length" class="rounded mt-3 overflow-hidden" style="border:1px solid #e2e8f0;">
			<div class="d-flex align-items-baseline justify-content-between px-3 py-2"
				style="background:#f8fafc;border-bottom:1px solid #e2e8f0;font-size:12px;">
				<span class="fw-semibold" style="color:#334155;">{{ groups.length }} group{{ groups.length === 1 ? '' : 's' }} detected</span>
				<button type="button" @click="clearAll"
					class="btn btn-link btn-sm p-0 fw-normal text-decoration-none"
					style="font-size:11px;color:#64748b;">Clear all</button>
			</div>
			<ul class="list-unstyled mb-0">
				<li v-for="g in groups" :key="g.id"
					class="d-flex align-items-baseline justify-content-between gap-2 px-3 py-2"
					style="border-top:1px solid #f1f5f9;font-size:12px;">
					<div class="flex-grow-1 min-w-0">
						<div class="fw-medium text-truncate" style="color:#0f172a;">
							<span class="rounded text-uppercase fw-bold me-2 px-2 py-0"
								:style="g.kind === 'pdf'
									? { background:'#ffe4e6', color:'#9f1239', fontSize:'9px' }
									: { background:'#dbeafe', color:'#1e3a8a', fontSize:'9px' }">
								{{ g.kind }}
							</span>
							{{ g.baseName }}
							<span v-if="g.kind === 'images'" class="fw-normal" style="color:#64748b;">
								· {{ g.files.length }} page{{ g.files.length === 1 ? '' : 's' }}
							</span>
						</div>
						<div class="text-truncate ms-4" style="font-size:10px;color:#64748b;">
							{{ g.files.map(f => f.name).join(' · ') }}
						</div>
					</div>
					<div class="flex-shrink-0">
						<button type="button" @click="removeGroup(g)"
							class="btn btn-link p-0 text-decoration-none"
							style="color:#cbd5e1;font-size:14px;line-height:1;"
							title="Remove this group">×</button>
					</div>
				</li>
			</ul>
		</div>

		<div v-if="groups.length" class="d-flex justify-content-end mt-3">
			<button type="button" @click="processAll" :disabled="processing"
				class="btn fw-medium"
				style="background:#047857;color:white;border:none;font-size:12px;padding:6px 16px;">
				<span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
				{{ processing ? 'Parsing…' : `Parse ${groups.length} group${groups.length === 1 ? '' : 's'}` }}
			</button>
		</div>

		<!-- Results -->
		<div v-if="results.length" class="rounded mt-3 overflow-hidden" style="border:1px solid #e2e8f0;">
			<div class="d-flex align-items-baseline justify-content-between px-3 py-2"
				style="background:#f8fafc;border-bottom:1px solid #e2e8f0;font-size:12px;">
				<div>
					<span class="fw-semibold">Results:</span>
					<span class="fw-medium ms-2" style="color:#047857;">{{ stats.saved }} saved</span>
					<span class="ms-2" style="color:#64748b;">{{ stats.duplicate }} duplicate</span>
					<span v-if="stats.needsReview" class="ms-2" style="color:#92400e;">{{ stats.needsReview }} needs review</span>
					<span class="ms-2" style="color:#9f1239;">{{ stats.error }} error</span>
				</div>
				<span style="font-size:10px;color:#64748b;font-style:italic;">Parsed rows auto-save — nothing lives only in the browser.</span>
			</div>
			<table class="table table-sm mb-0" style="font-size:12px;">
				<thead style="background:rgba(248,250,252,0.7);">
					<tr style="color:#64748b;">
						<th class="fw-medium px-3 py-1">Group</th>
						<th class="fw-medium px-2 py-1">Status</th>
						<th class="fw-medium px-2 py-1">Muni</th>
						<th class="fw-medium px-2 py-1 text-end">Setup</th>
						<th class="fw-medium px-2 py-1 text-end">Annual</th>
						<th class="fw-medium px-2 py-1">Notes</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="r in results" :key="r.groupId" style="border-top:1px solid #f1f5f9;">
						<td class="fw-medium text-truncate px-3 py-1" style="color:#0f172a;max-width:240px;" :title="r.label">{{ r.label }}</td>
						<td class="px-2 py-1">
							<span v-if="r.status === 'saved'" class="rounded text-uppercase fw-bold px-2 py-0" style="font-size:9px;background:#047857;color:white;">Saved</span>
							<span v-else-if="r.status === 'duplicate'" class="rounded text-uppercase fw-bold px-2 py-0" style="font-size:9px;background:#fef3c7;color:#92400e;">Dup</span>
							<span v-else-if="r.status === 'needs-review'" class="rounded text-uppercase fw-bold px-2 py-0" style="font-size:9px;background:#fef3c7;color:#92400e;">Review</span>
							<span v-else-if="r.status === 'pending'" class="rounded text-uppercase fw-bold px-2 py-0" style="font-size:9px;background:#e0f2fe;color:#075985;">Parsing…</span>
							<span v-else class="rounded text-uppercase fw-bold px-2 py-0" style="font-size:9px;background:#ffe4e6;color:#9f1239;">Error</span>
						</td>
						<td class="px-2 py-1">
							<template v-if="r.data && r.data.parsed">
								<div class="fw-medium" style="color:#0f172a;">
									{{ (r.data.parsed.customer && (r.data.parsed.customer.city || r.data.parsed.customer.name)) || '?' }}<span class="fw-normal" style="color:#94a3b8;">, {{ (r.data.parsed.customer && r.data.parsed.customer.state) || '?' }}</span>
								</div>
								<div style="font-size:10px;color:#64748b;">
									pop {{ formatPop(r.data.parsed.customer && r.data.parsed.customer.population) }} · {{ r.data.parsed.contractYears || '?' }}yr · signed {{ r.data.parsed.signedDate || '?' }}
								</div>
							</template>
						</td>
						<td class="text-end font-monospace px-2 py-1" style="color:#475569;">
							<template v-if="r.data && r.data.verification">{{ fmtMoney(r.data.verification.sumSelectedSetup) }}</template>
						</td>
						<td class="text-end font-monospace fw-semibold px-2 py-1" style="color:#065f46;">
							<template v-if="r.data && r.data.verification">{{ fmtMoney(r.data.verification.sumSelectedAnnual) }}</template>
						</td>
						<td class="px-2 py-1" style="font-size:11px;color:#475569;">
							<template v-if="r.error"><span style="color:#9f1239;">{{ r.error }}</span></template>
							<template v-else-if="r.status === 'duplicate' && r.duplicateInfo">
								duplicate of <span class="fw-medium">{{ r.duplicateInfo.name }}, {{ r.duplicateInfo.state }}</span>
							</template>
							<template v-else-if="r.status === 'needs-review'">
								<span style="color:#92400e;">Missing city / state / signed date — fix the source and re-upload.</span>
							</template>
							<template v-else-if="r.status === 'saved' && r.proposalId">
								<router-link :to="`/cwpe/proposals/${r.proposalId}`" class="text-decoration-none" style="color:#047857;">
									saved · files attached
								</router-link>
							</template>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	</div>
</template>

<script>
import restApi from '../../api/index.js'

export default {
	name: 'CwpeBulkParsePanel',
	data() {
		return {
			dragOver: false,
			files: [],
			processing: false,
			results: [],
		}
	},
	computed: {
		groups() {
			return this.groupFiles(this.files)
		},
		stats() {
			const rs = this.results
			return {
				saved:       rs.filter(r => r.status === 'saved').length,
				duplicate:   rs.filter(r => r.status === 'duplicate').length,
				needsReview: rs.filter(r => r.status === 'needs-review').length,
				error:       rs.filter(r => r.status === 'error' && !!r.error).length,
			}
		},
	},
	methods: {
		// ── File grouping ──────────────────────────────────────────────
		// Mirrors the Nuxt BulkParsePanel rule: PDFs each = 1 group,
		// images grouped by normalized filename so multi-page scans
		// (Forestville-1.jpg, Forestville-2.jpg, …) become one proposal.
		normalizeBase(name) {
			return (name || '')
				.replace(/\.[^.]+$/, '')
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, ' ')
				.replace(/\b(page|pg|p)\s*\d+\b/g, '')
				.replace(/\b\d+\s*of\s*\d+\b/g, '')
				.replace(/\s+\d+\s*$/, '')
				.replace(/\s+/g, ' ')
				.trim() || 'group'
		},
		groupFiles(input) {
			const pdfs = input.filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'))
			const images = input.filter(f => f.type.startsWith('image/'))
			const out = []
			pdfs.forEach((pdf, i) => out.push({ id: `pdf-${i}-${pdf.name}`, kind: 'pdf', baseName: pdf.name, files: [pdf] }))

			const byBase = new Map()
			for (const img of images) {
				const base = this.normalizeBase(img.name)
				if (!byBase.has(base)) byBase.set(base, [])
				byBase.get(base).push(img)
			}
			let imgIdx = 0
			for (const [base, imgs] of byBase) {
				imgs.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
				out.push({ id: `img-${imgIdx++}-${base}`, kind: 'images', baseName: base, files: imgs })
			}
			return out
		},
		addFiles(selected) {
			const list = Array.from(selected || [])
			const accepted = list.filter(f =>
				f.type === 'application/pdf'
				|| f.name.toLowerCase().endsWith('.pdf')
				|| f.type.startsWith('image/'),
			)
			const existing = new Set(this.files.map(f => `${f.name}|${f.size}|${f.lastModified}`))
			const next = [...this.files]
			for (const f of accepted) {
				const key = `${f.name}|${f.size}|${f.lastModified}`
				if (!existing.has(key)) { next.push(f); existing.add(key) }
			}
			this.files = next
		},
		onDrop(e) {
			this.dragOver = false
			if (e.dataTransfer && e.dataTransfer.files) this.addFiles(e.dataTransfer.files)
		},
		onFilePick(e) {
			if (e.target.files) this.addFiles(e.target.files)
			e.target.value = ''
		},
		removeGroup(group) {
			const toRemove = new Set(group.files)
			this.files = this.files.filter(f => !toRemove.has(f))
		},
		clearAll() {
			this.files = []
			this.results = []
		},

		// ── Base64 encoding for image upload ───────────────────────────
		fileToBase64(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader()
				reader.onload = () => {
					const out = String(reader.result || '')
					const idx = out.indexOf(',')
					const data = idx >= 0 ? out.slice(idx + 1) : out
					let media = file.type || 'application/octet-stream'
					if (media === 'image/jpg') media = 'image/jpeg'
					resolve({ data, mediaType: media })
				}
				reader.onerror = () => reject(new Error('read failed'))
				reader.readAsDataURL(file)
			})
		},

		// ── Process one group ──────────────────────────────────────────
		async parseGroup(group) {
			const label = `${group.baseName} (${group.kind === 'pdf' ? 'PDF' : `${group.files.length} page${group.files.length === 1 ? '' : 's'}`})`
			try {
				let res
				if (group.kind === 'pdf') {
					const fd = new FormData()
					fd.append('file', group.files[0])
					res = await restApi.post('/cwpe/admin/proposals/bulk-parse-pdf', fd, {
						headers: { 'content-type': 'multipart/form-data' },
					})
				} else {
					const images = await Promise.all(group.files.map(async (f) => {
						const { data, mediaType } = await this.fileToBase64(f)
						return { data, mediaType, filename: f.name }
					}))
					res = await restApi.post('/cwpe/admin/proposals/bulk-parse-images', { images })
				}
				const r = res.data
				return {
					groupId: group.id,
					label,
					status: this.statusFromResponse(r),
					data: r,
					duplicateInfo: r.duplicateInfo || null,
					reason: r.reason,
					proposalId: r.proposalId,
				}
			} catch (e) {
				const errBody = e && e.response && e.response.data
				return {
					groupId: group.id,
					label,
					status: 'error',
					error: (errBody && (errBody.error || errBody.message)) || e.message || 'Parse failed',
				}
			}
		},
		statusFromResponse(r) {
			if (r && r.saved) return 'saved'
			if (r && r.duplicateOf) return 'duplicate'
			if (r && r.reason === 'missing-required-fields') return 'needs-review'
			return 'error'
		},
		async processAll() {
			this.processing = true
			this.results = []
			let anySaved = false
			try {
				// Sequential processing — Anthropic rate limits + each parse
				// auto-saves so a mid-batch reload keeps everything completed.
				for (const g of this.groups) {
					this.results.push({ groupId: g.id, label: `Parsing ${g.baseName}…`, status: 'pending' })
					const r = await this.parseGroup(g)
					const idx = this.results.findIndex(x => x.groupId === g.id)
					if (idx >= 0) this.$set(this.results, idx, r)
					if (r.status === 'saved') anySaved = true
				}
				this.files = []
				if (anySaved) this.$emit('imported')
			} finally {
				this.processing = false
			}
		},

		// ── Formatters ─────────────────────────────────────────────────
		fmtMoney(n) {
			if (n == null || isNaN(n)) return ''
			const abs = Math.abs(Math.round(Number(n))).toLocaleString('en-US', { maximumFractionDigits: 0 })
			return n < 0 ? `−$${abs}` : `$${abs}`
		},
		formatPop(n) {
			if (n == null || isNaN(n)) return '?'
			return Number(n).toLocaleString('en-US')
		},
	},
}
</script>
